/* eslint-disable react/button-has-type */
import React, {
  useState, useRef, useCallback,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { useSpring, animated } from 'react-spring';

import { ColorGray, TransitionGlobal } from '../../../../shared/GlobalStyles';
import SearchIcon from '../../../../shared/images/magnifying-glass.svg';
import IconRound from '../../../../shared/components/IconRound/IconRound';

import { setSearchString } from '../../ducks/feedActions';
import { feedTracker } from '../../../../shared/utils/amplitudeHelper';

const ClearSearchButton = styled('button')`
  margin: right;
  border: none;
`;

const IconWrapper = styled('div')`
  display: flex;
`;

const SearchInput = styled('input')`
  font-family: 'Open Sans';
  width: 100%;
  border: none;
  background-image: none;
  background-color: transparent;
  -webkit-box-shadow: none;
  -moz-box-shadow: none;
  box-shadow: none;
  height: 30px;
  color: ${(props) => props.theme.colors.text};
`;

const AnimatedSearchContainer = styled(animated.div)`
  font-family: 'Open Sans';
  box-shadow: 0 4px 8px 0 rgba(48, 49, 51, 0.05);
  border-radius: 21px;
  background-color: ${(props) => props.theme.colors.background[1]};
  background-image: url(${SearchIcon});
  background-repeat: no-repeat;
  background-size: 20px;
  color: ${(props) => props.theme.colors.text};
  width: 100%;
  &::placeholder {
    color: ${ColorGray};
  }

  @media screen and (orientation: portrait) {
    width: 180px;
  }
  
  @media screen and (max-width: 601px) {
    background-size: 18px!important;
    width: 140px;
    padding: 6px 10px 6px 45px;
  }
`;

const SearchWrapper = styled('div')`
  display: flex;
  position: relative;
  &:focus {
    outline: none;
  }
  .icon {
    position: absolute;
    top: 9px;
    right: 8px;
    opacity: ${(props) => (props.isSearchActive ? '1' : '0')};
    transition: ${TransitionGlobal};
    cursor: pointer;
    z-index: ${(props) => (props.isSearchActive ? '1000' : '-1000')};
  }
`;

const useFocus = () => {
  const htmlElRef = useRef(null);
  const setFocus = () => (htmlElRef.current && htmlElRef.current.focus());

  return [htmlElRef, setFocus];
};

const FeedPostSearch = ({
  isClearable,
}) => {
  const [inputRef, setInputFocus] = useFocus();
  const searchString = useSelector((state) => state.feed.data.searchString);
  const [searchValue, setSearchValue] = useState(searchString);
  const [isSearchActive, setSearchActive] = useState(false);

  const dispatch = useDispatch();

  const isSearched = searchValue !== '' && searchValue === searchString;

  const expandSearch = useCallback(() => {
    setSearchActive(true);
    // Brief delay to allow search container to expand before focusing input
    setTimeout(() => setInputFocus(), 200);
  }, []);

  const collapseSearch = useCallback(() => {
    setSearchActive(false);
    feedTracker('Normal Mode', 'Search collapsed');
  }, []);

  const handleInputChange = (e) => {
    setSearchValue(e.target.value);
  };

  const handleInputBlur = (e) => {
    if (e.target.value.length === 0 && !isSearched) {
      collapseSearch();
    }
  };

  const handleInputKeyUp = (e) => {
    setSearchValue(e.target.value);
    if (e.key === 'Enter') {
      dispatch(setSearchString(searchValue));
      feedTracker('Normal Mode', 'Search entered');
    }
  };

  const handleClearSearch = (e) => {
    e.stopPropagation();
    if (isSearched) {
      dispatch(setSearchString(''));
      feedTracker('Normal Mode', 'Search cleared');
    } else if (!isSearched && searchValue === '') {
      collapseSearch();
    }
    setSearchValue('');
  };

  const props = useSpring({
    to: {
      width: isSearchActive ? 180 : 42,
      padding: isSearchActive ? '5px 30px 5px 45px' : '5px 10px 5px 30px',
      backgroundPosition: isSearchActive ? '20px 10px' : '11px 10px',
    },
    from: {
      width: 0,
      padding: '5px 20px 5px 40px',
      backgroundPosition: '11px 10px',
      position: 'relative',
    },
  });

  return (
    <SearchWrapper
      isClearable={isClearable}
      isSearchActive={isSearchActive}
      onClick={expandSearch}
    >
      <AnimatedSearchContainer style={props}>
        <SearchInput
          ref={inputRef}
          placeholder='Search'
          onChange={handleInputChange}
          value={searchValue}
          values={searchValue}
          tabIndex='0'
          onBlur={handleInputBlur}
          onKeyUp={handleInputKeyUp}
          aria-label='Search posts'
        />
        {isClearable && (
          <IconWrapper>
            <ClearSearchButton
              type='reset'
              onClick={handleClearSearch}
              aria-label='Clear search'
            >
              <IconRound
                className='icon'
                icon='remove'
                noBorder
                size={10}
              />
            </ClearSearchButton>
          </IconWrapper>
        )}
      </AnimatedSearchContainer>
    </SearchWrapper>
  );
};

FeedPostSearch.propTypes = {
  isClearable: PropTypes.bool.isRequired,
};

export default FeedPostSearch;
